import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner } from 'react-bootstrap'
import { vendorAPI } from '../api/vendorAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function VendorsListScreen() {
    const { hasPermission } = usePermissions()
    const [vendors, setVendors] = useState([])
    const [showDeactivated, setShowDeactivated] = useState(false)
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('vendorColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            name: 'auto',
            full_name: 'auto',
            contact: 'auto',
            bill_to_address: 'auto',
            ship_to_address: 'auto',
            phone: 'auto',
            email: 'auto',
            shipping_lead_time_days: 100,
            actions: 100
        };
    });

    useEffect(() => {
        localStorage.setItem('vendorColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchVendors() {
            try {
                setLoading(true)
                const response = await vendorAPI.getAll(currentPage, pageSize, searchTerm, showDeactivated)
                setVendors(response.results)
                setTotalPages(response.count)
            } finally {
                setLoading(false)
            }
        }
        fetchVendors()
    }, [currentPage, pageSize, searchTerm, showDeactivated])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handleToggleActive = async (e, vendor) => {
        e.stopPropagation();
        
        if (vendor.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this vendor?')) {
                return;
            }
        }

        try {
            const updatedVendor = {
                ...vendor,
                is_active: !vendor.is_active
            };
            
            await vendorAPI.update(vendor._id, updatedVendor);
            
            setVendors(vendors.map(v => 
                v._id === vendor._id 
                    ? updatedVendor
                    : v
            ));
        } catch (error) {
            console.error('Error toggling vendor status:', error);
            alert(`Failed to update vendor: ${error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Vendors</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search vendors..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button 
                        variant="outline-secondary"
                        onClick={() => setShowDeactivated(!showDeactivated)}
                        className="me-2"
                    >
                        {showDeactivated ? 'Hide Deactivated' : 'Show Deactivated'}
                    </Button>
                    {hasPermission('vendors.modify') && (
                        <Button
                            onClick={() => navigate('/vendor/create')}
                            variant="success"
                        >
                            Create Vendor
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="vendors_table"
                        columns={[
                            {
                                id: 'name',
                                header: 'Name',
                                cell: (row) => (
                                    <>
                                        {highlightText(row.name, searchTerm)}
                                        {!row.is_active && (
                                            <span className="ms-2 text-muted fst-italic">
                                                (Inactive)
                                            </span>
                                        )}
                                    </>
                                )
                            },
                            {
                                id: 'full_name',
                                header: 'Full Name',
                                cell: (row) => highlightText(row.full_name, searchTerm)
                            },
                            {
                                id: 'contact',
                                header: 'Contact',
                                cell: (row) => highlightText(row.contact, searchTerm)
                            },
                            {
                                id: 'bill_to_address',
                                header: 'Bill To Address',
                                cell: (row) => highlightText(row.bill_to_address, searchTerm)
                            },
                            {
                                id: 'ship_to_address',
                                header: 'Ship To Address',
                                cell: (row) => highlightText(row.ship_to_address, searchTerm)
                            },
                            {
                                id: 'phone',
                                header: 'Phone',
                                cell: (row) => highlightText(row.phone, searchTerm)
                            },
                            {
                                id: 'email',
                                header: 'Email',
                                cell: (row) => highlightText(row.email, searchTerm)
                            },
                            {
                                id: 'shipping_lead_time_days',
                                header: 'Lead Time',
                                cell: (row) => row.shipping_lead_time_days != null ? `${row.shipping_lead_time_days} days` : '-'
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    hasPermission('vendors.modify') && (
                                        <Button
                                            variant={row.is_active ? "danger" : "success"}
                                            size="sm"
                                            onClick={(e) => handleToggleActive(e, row)}
                                        >
                                            {row.is_active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                    )
                                )
                            }
                        ]}
                        data={vendors}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/vendor/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default VendorsListScreen